import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Panel from '@/components/ui/Panel';
import Toast from '@/components/ui/Toast';
import { shortDate } from '@/lib/format';
import { portalClaims, portalPartner } from '@/mocks/portal';

export default function PortalClaimDetailPage() {
  const { t } = useTranslation();
  const { claim } = useParams<{ claim: string }>();
  const found = portalClaims.find((c) => c.claim === claim);

  const [status, setStatus] = useState(found?.status || 'Open');
  const [thread, setThread] = useState(found?.thread || []);
  const [reply, setReply] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  if (!found) {
    return (
      <div>
        <Link to="/portal/warranty" className="inline-flex items-center gap-1 text-[12px] font-medium text-foreground-600 hover:text-primary-700">
          <i className="ri-arrow-left-line text-[13px]" />
          {t('portal.warranty.back')}
        </Link>
        <p className="mt-6 rounded-lg border border-background-200 bg-background-50 px-4 py-10 text-center text-[13px] text-foreground-500">
          {t('portal.warranty.notFound')}
        </p>
      </div>
    );
  }

  const agree = () => {
    setStatus('ReplacementAgreed');
    setToast(t('portal.warranty.agreedToast'));
  };

  const sendReply = () => {
    const body = reply.trim();
    if (!body) return;
    setThread((prev) => [
      ...prev,
      {
        id: `local-${prev.length + 1}`,
        author: 'partner',
        name: portalPartner.user,
        body,
        when: '2026-10-24 10:20',
      },
    ]);
    setReply('');
    setToast(t('portal.warranty.replyToast'));
  };

  return (
    <div>
      <Link to="/portal/warranty" className="inline-flex items-center gap-1 text-[11.5px] font-medium text-foreground-500 hover:text-primary-700">
        <i className="ri-arrow-left-line text-[13px]" />
        {t('portal.warranty.back')}
      </Link>

      <div className="mb-4 mt-2 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="tnum text-[21px] font-semibold leading-7 text-foreground-950">{found.claim}</h1>
            <PortalStatus status={status} />
          </div>
          <p className="mt-1 text-[11.5px] text-foreground-500">
            {t('portal.warranty.claimMeta', {
              po: found.po,
              sku: found.sku,
              date: shortDate(found.updated),
            })}
          </p>
        </div>
        {status !== 'ReplacementAgreed' && status !== 'Closed' && (
          <button
            type="button"
            onClick={agree}
            className="inline-flex h-8 w-fit items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-shield-check-line text-[14px]" />
            {t('portal.warranty.agree')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="flex flex-col gap-4 xl:col-span-2">
          <Panel padded={false}>
            <div className="border-b border-background-200 px-4 py-3">
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.warranty.description')}</h2>
            </div>
            <div className="px-4 py-3">
              <p className="text-[12.5px] leading-6 text-foreground-700">{found.description}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span className="tnum rounded border border-background-200 bg-background-100 px-2 py-1 font-medium text-foreground-700">
                  {found.sku} · {found.item}
                </span>
                <span className="tnum rounded border border-background-200 bg-background-100 px-2 py-1 text-foreground-600">
                  {found.po}
                </span>
              </div>
            </div>
          </Panel>

          <Panel padded={false}>
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.warranty.photos')}</h2>
              <span className="text-[10.5px] text-foreground-400">{found.photos.length}</span>
            </div>
            <div className="p-3">
              {found.photos.length > 0 ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {found.photos.map((src, index) => (
                    <a
                      key={src}
                      href={src}
                      target="_blank"
                      rel="noreferrer"
                      className="group relative block h-[180px] w-full overflow-hidden rounded-md border border-background-200 bg-background-100"
                    >
                      <img src={src} alt={`${found.claim} photo ${index + 1}`} className="h-full w-full object-top" />
                      <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 transition-colors group-hover:bg-foreground-950/20">
                        <i className="ri-zoom-in-line text-[18px] text-background-50 opacity-0 transition-opacity group-hover:opacity-100" />
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="py-4 text-center text-[11.5px] text-foreground-500">{t('portal.warranty.photos')}: —</p>
              )}
            </div>
          </Panel>

          <Panel padded={false}>
            <div className="border-b border-background-200 px-4 py-3">
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.warranty.reply')}</h2>
            </div>
            <div className="max-h-[260px] overflow-y-auto thin-scroll px-4 py-3">
              <div className="flex flex-col gap-2.5">
                {thread.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[92%] rounded-lg border px-2.5 py-2 ${
                      m.author === 'partner'
                        ? 'ml-auto border-primary-200 bg-primary-50'
                        : 'border-background-200 bg-background-100'
                    }`}
                  >
                    <p className="text-[10px] font-semibold text-foreground-700">{m.name}</p>
                    <p className="mt-0.5 text-[11.5px] leading-4 text-foreground-800">{m.body}</p>
                    <p className="tnum mt-1 text-[9px] text-foreground-400">{m.when}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-background-200 p-3">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value.slice(0, 500))}
                rows={3}
                placeholder={t('portal.warranty.replyPlaceholder')}
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-[11.5px] outline-none focus:border-primary-400"
              />
              <button
                type="button"
                onClick={sendReply}
                className="mt-1.5 inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-3 text-[11.5px] font-semibold text-background-50 hover:bg-foreground-900"
              >
                <i className="ri-send-plane-line text-[12px]" />
                {t('portal.warranty.send')}
              </button>
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-4">
          <Panel>
            <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.warranty.resolution')}</h2>
            <dl className="mt-2 space-y-2.5 text-[11.5px]">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-foreground-500">{t('portal.warranty.colStatus')}</dt>
                <dd>
                  <PortalStatus status={status} />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-foreground-500">{t('portal.warranty.colPo')}</dt>
                <dd className="tnum font-medium text-foreground-900">{found.po}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-foreground-500">{t('portal.common.sku')}</dt>
                <dd className="tnum font-medium text-primary-700">{found.sku}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-foreground-500">{t('portal.warranty.colUpdated')}</dt>
                <dd className="tnum font-medium text-foreground-900">{shortDate(found.updated)}</dd>
              </div>
            </dl>
            {status === 'ReplacementAgreed' && (
              <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] font-semibold text-emerald-800">{t('portal.warranty.resolveTitle')}</p>
                <p className="mt-0.5 text-[10.5px] leading-4 text-emerald-800/80">
                  {t('portal.warranty.resolveBody', { sku: found.sku })}
                </p>
              </div>
            )}
            <p className="mt-3 border-t border-background-200 pt-2.5 text-[10px] leading-4 text-foreground-400">
              {t('portal.warranty.warrantyNote')}
            </p>
          </Panel>
        </div>
      </div>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}